import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { ChangeChip } from "@similarweb/ui-components";
import { Flex, Text, Stack } from "@similarweb/ui-components/foundations";

import type { HeatmapTopic } from "app/pages/market-research/api-client/topicsHeatmapAPI";

import { changeFormatter, getGrowthSentiment, volumeFormatter } from "./utils";

type TrendData = HeatmapTopic["trend"];

const TooltipWrapper = styled.div`
    padding: 12px;
    min-width: 180px;
`;

const SparklineSvg = styled.svg`
    display: block;
`;

const SPARKLINE_WIDTH = 140;
const SPARKLINE_HEIGHT = 32;

const buildSparklinePath = (trend: TrendData): string => {
    if (!trend?.length) return "";
    if (trend.length === 1) {
        return `M 0,${SPARKLINE_HEIGHT / 2} L ${SPARKLINE_WIDTH},${SPARKLINE_HEIGHT / 2}`;
    }

    const volumes = trend.map((d) => d.currentPeriod.volume);
    const max = Math.max(...volumes);
    const min = Math.min(...volumes);
    const range = max - min || 1;

    return volumes
        .map((v, i) => {
            const x = (i / (volumes.length - 1)) * SPARKLINE_WIDTH;
            const y = SPARKLINE_HEIGHT - ((v - min) / range) * SPARKLINE_HEIGHT;
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
};

type TopicsHeatmapTooltipProps = {
    name: string;
    volume: number;
    change: number | null;
    growth: string;
    trend: TrendData;
};

export const TopicsHeatmapTooltip = ({ name, volume, change, growth, trend }: TopicsHeatmapTooltipProps) => {
    const sparklinePath = buildSparklinePath(trend);
    const strokeColor =
        growth === "Growing"
            ? colorsPalettes.green[400]
            : growth === "Declining"
              ? colorsPalettes.red[400]
              : colorsPalettes.carbon[300];

    return (
        <TooltipWrapper>
            <Stack gap="8px">
                <Text
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[500]}
                >
                    {name}
                </Text>
                <Flex
                    gap="4px"
                    alignItems="center"
                    flexDirection="row"
                >
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {volumeFormatter(volume)}
                    </Text>
                    <ChangeChip
                        sentiment={getGrowthSentiment(growth)}
                        size="small"
                    >
                        {changeFormatter(change)}
                    </ChangeChip>
                </Flex>
                {sparklinePath && (
                    <Stack padding={"10px 0px"}>
                        <SparklineSvg
                            width={SPARKLINE_WIDTH}
                            height={SPARKLINE_HEIGHT}
                            viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
                        >
                            <path
                                d={sparklinePath}
                                fill="none"
                                stroke={strokeColor}
                                strokeWidth={1.5}
                            />
                        </SparklineSvg>
                    </Stack>
                )}
            </Stack>
        </TooltipWrapper>
    );
};
